import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Card,
  CardContent,
} from "@mui/material";
import {
  formatCurrency,
  formatDate,
  formatVehicleNumber,
} from "../../../utils/utils";
import { useQuery } from "@tanstack/react-query";
import { getValidToken } from "../../../utils/authUtils";
import axios from "axios";
import { useReportFilter } from "../context/ReportFilterContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { Loading } from "../../../components/Loading";

interface ApiResponse {
  isSuccess: boolean;
  message: string;
  data: ReportData;
}

interface AnalyticsEntity {
  totalAmount: number;
  advanceAmount: number;
  settlementAmount: number;
  balance: number;
  tripCount: number;
}

interface TripExpense {
  id: string;
  type: string;
  paidTo: string;
  amount: string;
  description: string;
  advanceAmount: number;
  settlementAmount: number;
  balance: number;
  contactNumber?: string;
  payments: Array<{
    id: string;
    amount: string;
    paymentType: string;
    paymentDate: string;
  }>;
}

interface Trip {
  id: string;
  journeyStartDate: string;
  journeyReturnDate: string;
  fromLocation: string;
  toLocation: string;
  tripType: string;
  vehicle: {
    id: string;
    vehicleNumber: string;
    vehicleType: string;
  };
  tripExpenses: TripExpense[];
}

interface ReportData {
  trips: Trip[];
  analytics: {
    byEntity: Record<string, AnalyticsEntity>;
    graphData: Array<AnalyticsEntity & { entity: string }>;
  };
}

const NoDataMessage = () => (
  <Typography
    textAlign="center"
    display="flex"
    justifyContent="center"
    alignItems="center"
    variant="caption"
    sx={{
      fontSize: "0.75rem",
      color: "text.secondary",
      px: 1.5,
      pb: 1,
      backgroundColor: "white",
      width: "100%",
      height: "100%",
      minHeight: "100px",
    }}
  >
    No Driver/Cleaner data available
  </Typography>
);

const DriverCleanerReport: React.FC = () => {
  const { filterState } = useReportFilter();
  const {
    timeFilter,
    customStartDate,
    customEndDate,
    selectedVehicles,
    driverCleanerName,
  } = filterState;
  const navigate = useNavigate();
  const [expandedPanel, setExpandedPanel] = useState<string | false>(false);
  const handleAccordionChange =
    (tripId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanel(isExpanded ? tripId : false);
    };

  const {
    data: apiResponse,
    isLoading,
    isFetching,
  } = useQuery<ApiResponse>({
    queryKey: [
      "driverCleanerReport",
      timeFilter,
      customStartDate,
      customEndDate,
      selectedVehicles,
      driverCleanerName,
    ],
    queryFn: async () => {
      const token = await getValidToken();
      let url = `${process.env.REACT_APP_API_HOST}reports/driver-cleaner/${timeFilter}`;

      if (timeFilter === "custom") {
        url = `${process.env.REACT_APP_API_HOST}reports/driver-cleaner/custom`;
      }

      const params: any = {};
      if (timeFilter === "custom") {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }
      if (selectedVehicles?.length > 0) {
        params.vehicleNumbers = selectedVehicles;
      }
      if (driverCleanerName) {
        params.driverCleanerName = driverCleanerName;
      }
      const response = await axios.get(url, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
    enabled: timeFilter !== "custom" || (!!customStartDate && !!customEndDate),
  });

  const reportData = apiResponse?.data;

  const hasData =
    reportData &&
    (Object.keys(reportData.analytics?.byEntity || {}).length > 0 ||
      (reportData.trips || []).length > 0);

  if (!hasData) {
    return (
      <Box sx={{ py: 2 }}>
        <NoDataMessage />
      </Box>
    );
  }

  const renderAnalyticsSummary = () => {
    const analytics = reportData?.analytics?.byEntity || {};
    if (Object.keys(analytics).length === 0) return null;

    const totalTrips = Object.values(analytics).reduce(
      (sum, entity) => sum + entity.tripCount,
      0
    );
    const totalAmount = Object.values(analytics).reduce(
      (sum, entity) => sum + entity.totalAmount,
      0
    );
    const totalAdvance = Object.values(analytics).reduce(
      (sum, entity) => sum + entity.advanceAmount,
      0
    );
    const totalBalance = Object.values(analytics).reduce(
      (sum, entity) => sum + entity.balance,
      0
    );

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Paper
            sx={{
              p: 1,
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              py: 1.5,
            }}
          >
            <Typography fontSize={12}>Total Trips</Typography>
            <Typography variant="h4" fontSize={15}>
              {totalTrips}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper
            sx={{
              p: 1,
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              py: 1.5,
            }}
          >
            <Typography fontSize={12}>Total Amount</Typography>
            <Typography variant="h4" fontSize={15}>
              {formatCurrency(totalAmount)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper
            sx={{
              p: 1,
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              py: 1.5,
            }}
          >
            <Typography fontSize={12}>Total Advance</Typography>
            <Typography variant="h4" fontSize={15}>
              {formatCurrency(totalAdvance)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper
            sx={{
              p: 1,
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              py: 1.5,
            }}
          >
            <Typography fontSize={12}>Total Balance</Typography>
            <Typography variant="h4" fontSize={15}>
              {formatCurrency(totalBalance)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  const renderAnalyticsGraph = () => {
    const graphData = reportData?.analytics?.graphData || [];
    if (graphData.length <= 1) return null;

    return (
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom fontSize={16}>
          Driver/Cleaner Overview
        </Typography>
        <Box sx={{ width: "100%", height: 300 }}>
          <ResponsiveContainer>
            <BarChart data={graphData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="entity" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="totalAmount" name="Total Amount" fill="#8884d8" />
              <Bar dataKey="advanceAmount" name="Advance" fill="#82ca9d" />
              <Bar
                dataKey="settlementAmount"
                name="Settlement"
                fill="#82ca9d"
              />
              <Bar dataKey="balance" name="Balance" fill="#ffc658" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Paper>
    );
  };

  const renderAnalyticsTable = () => {
    const analytics = reportData?.analytics?.byEntity || {};
    if (Object.keys(analytics).length === 0) return null;

    return (
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom fontSize={16}>
          Driver/Cleaner Summary
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeaderStyle}>Name</TableCell>
                <TableCell sx={tableHeaderStyle}>Total Trips</TableCell>
                <TableCell sx={tableHeaderStyle}>Total Amount</TableCell>
                <TableCell sx={tableHeaderStyle}>Advance</TableCell>
                <TableCell sx={tableHeaderStyle}>Settlement</TableCell>
                <TableCell sx={tableHeaderStyle}>Balance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(analytics).map(([name, data]) => (
                <TableRow key={name}>
                  <TableCell sx={tableCellStyle}>
                    <Typography
                      sx={{
                        cursor: "pointer",
                        textDecoration: "underline",
                        color: "primary.main",
                        fontSize: tableCellStyle.fontSize,
                      }}
                      onClick={() => {
                        navigate(`/master-data/staff/DRIVER`);
                      }}
                    >
                      {name}
                    </Typography>
                  </TableCell>
                  <TableCell sx={tableCellStyle}>{data.tripCount}</TableCell>
                  <TableCell sx={tableCellStyle}>
                    {formatCurrency(data.totalAmount)}
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    {formatCurrency(data.advanceAmount)}
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    {formatCurrency(data.settlementAmount)}
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    {formatCurrency(data.balance)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  const renderDriverCleanerDetails = (item: Trip) => {
    if (!item.tripExpenses?.length) return <NoDataMessage />;

    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeaderStyle}>Name</TableCell>
              <TableCell sx={tableHeaderStyle}>Contact</TableCell>
              <TableCell sx={tableHeaderStyle}>Staff Type</TableCell>
              <TableCell sx={tableHeaderStyle}>Amount</TableCell>
              <TableCell sx={tableHeaderStyle}>Payment History</TableCell>
              <TableCell sx={tableHeaderStyle}>Balance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {item.tripExpenses.map((expense) => (
              <TableRow key={expense.id}>
                <TableCell sx={tableCellStyle}>
                  {expense.paidTo || "-"}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {expense.contactNumber || "-"}
                </TableCell>
                <TableCell sx={tableCellStyle}>{expense.type || "-"}</TableCell>
                <TableCell sx={tableCellStyle}>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.primary">
                      {formatCurrency(parseFloat(expense.amount))}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  <Stack spacing={0.5}>
                    {expense.payments.map((payment) => (
                      <Typography
                        key={payment.id}
                        variant="caption"
                        color="text.primary"
                      >
                        {payment.paymentType}:{" "}
                        {formatCurrency(parseFloat(payment.amount))} (
                        {formatDate(payment.paymentDate)})
                      </Typography>
                    ))}
                    {expense.payments.length === 0 && (
                      <Typography variant="caption" color="text.primary">
                        No payments
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  <Typography variant="caption" color="text.primary">
                    {formatCurrency(expense.balance)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderTripDetails = () => {
    if (!reportData?.trips?.length) return null;

    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom fontSize={16}>
          Trip Details
        </Typography>
        <Box sx={{ mt: 2 }}>
          {reportData.trips.map((item) => (
            <Accordion
              key={item.id}
              expanded={expandedPanel === item.id}
              onChange={handleAccordionChange(item.id)}
              sx={{
                mb: 1,
                "&:before": {
                  display: "none",
                },
                boxShadow: "none",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <AccordionSummary
                sx={{
                  "& .MuiAccordionSummary-content": {
                    justifyContent: "space-between",
                    alignItems: "center",
                  },
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontSize: tableCellStyle.fontSize,
                        cursor: "pointer",
                        textDecoration: "underline",
                        color: "primary.main",
                      }}
                      onClick={() => {
                        navigate(`/trip/daily-bus/${item.id}/expense`);
                      }}
                    >
                      {item?.fromLocation} - {item?.toLocation}:{" "}
                      {item?.tripType}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" sx={{ fontSize: "12px" }}>
                      Start: {formatDate(item.journeyStartDate)}
                      {item.journeyReturnDate && (
                        <>
                          {" - "}Return: {formatDate(item.journeyReturnDate)}
                        </>
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" sx={{ fontSize: "12px" }}>
                      Vehicle:{" "}
                      {formatVehicleNumber(item.vehicle?.vehicleNumber)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <Typography
                      variant="body2"
                      color="primary"
                      sx={{
                        textAlign: "right",
                        whiteSpace: "nowrap",
                        fontSize: "12px",
                      }}
                    >
                      {expandedPanel === item.id ? "Hide" : "Show Details"}
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, py: 0 }}>
                {renderDriverCleanerDetails(item)}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Paper>
    );
  };

  return (
    <Box>
      {isLoading && (
        <Loading
          fullScreen={false}
          message="Loading driver/cleaner report..."
        />
      )}
      {isFetching && !isLoading && (
        <Loading
          fullScreen={false}
          message="Refreshing driver/cleaner report..."
        />
      )}
      {renderAnalyticsSummary()}
      {/* {renderAnalyticsGraph()} */}
      {renderAnalyticsTable()}
      {renderTripDetails()}
    </Box>
  );
};

export default DriverCleanerReport;

const tableHeaderStyle = {
  padding: "12px 16px",
  textAlign: "left" as const,
  backgroundColor: "#f5f5f5",
  borderBottom: "1px solid #ddd",
  fontSize: "12px",
};

const tableCellStyle = {
  borderBottom: "1px solid #ddd",
  fontSize: "12px",
};
